/**
 * REST fetch wrappers. Every request carries the wp_rest nonce in X-WP-Nonce.
 * A 403 signals an expired nonce (session-expired notice); a network failure
 * surfaces as a generic retry notice.
 */

import type { IewConfig, ValuesMap, ValidateStepResponse, SubmitResponse } from './types';

export class WizardApi {
  constructor(private readonly config: IewConfig) {}

  async validateStep(step: number, values: ValuesMap, filesPresent: string[]): Promise<ValidateStepResponse> {
    const res = await fetch(`${this.config.restUrl}validate-step`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': this.config.nonce,
      },
      body: JSON.stringify({ step, lang: this.config.lang, values, files_present: filesPresent }),
    });

    if (res.status === 403) {
      throw new SessionExpiredError();
    }

    return (await res.json()) as ValidateStepResponse;
  }

  async submitForm(form: HTMLFormElement): Promise<SubmitResponse> {
    const data = new FormData(form);
    // lang already travels as a hidden field, but ensure it is present.
    if (!data.has('lang')) {
      data.append('lang', this.config.lang);
    }

    // NOTE: do NOT set Content-Type — the browser adds the multipart boundary.
    const res = await fetch(`${this.config.restUrl}submit`, {
      method: 'POST',
      headers: { 'X-WP-Nonce': this.config.nonce },
      body: data,
    });

    if (res.status === 403) {
      throw new SessionExpiredError();
    }

    return (await res.json()) as SubmitResponse;
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('session expired');
    this.name = 'SessionExpiredError';
  }
}
